import { useCallback, useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';

import type { TopologyPathEndpointSearchRequest, TopologyPathEndpointSearchResponse } from '@app/types/sdk';
import { resolveErrorMessage } from '@app/utils/errors';

type SearchSide = TopologyPathEndpointSearchRequest['side'];
type SearchCoverage = NonNullable<TopologyPathEndpointSearchResponse['coverage']>;
type SearchItem = TopologyPathEndpointSearchResponse['items'][number];

interface PingMeshEndpointAutocompleteProps {
  side: SearchSide;
  label: string;
  placeholder: string;
  value: string;
  disabled?: boolean;
  suggestionsEnabled?: boolean;
  showLabel?: boolean;
  inputClassName?: string;
  buildRequest: (query: string) => TopologyPathEndpointSearchRequest;
  search: (request: TopologyPathEndpointSearchRequest, signal: AbortSignal) => Promise<TopologyPathEndpointSearchResponse>;
  onInputChange: (value: string) => void;
  onSelect: (item: SearchItem) => void;
}

const isAbortError = (error: unknown): boolean =>
  error instanceof DOMException ? error.name === 'AbortError' : error instanceof Error ? error.name === 'AbortError' : false;

export const hasAtLeastTwoIPv4Octets = (value: string): boolean => {
  const octets = value.trim().split('.');
  if (octets.length < 2) {
    return false;
  }
  return octets.slice(0, 2).every((octet) => {
    if (!/^\d{1,3}$/.test(octet)) {
      return false;
    }
    const numericValue = Number(octet);
    return numericValue >= 0 && numericValue <= 255;
  });
};

export const hasAtLeastTwoIPv6Hextets = (value: string): boolean => {
  const query = value.trim();
  if (!query.includes(':')) {
    return false;
  }
  if (typeof URL !== 'undefined') {
    try {
      const parsed = new URL(`http://[${query}]/`);
      if (parsed.hostname.startsWith('[') && parsed.hostname.endsWith(']')) {
        return true;
      }
    } catch {
      // Partial IPv6 prefixes are expected while the user types.
    }
  }
  const hextets = query.split(':').filter(Boolean);
  return hextets.length >= 2 && hextets.every((hextet) => /^[0-9a-f]{1,4}$/i.test(hextet));
};

export const hasPingMeshAddressPrefix = (value: string): boolean =>
  hasAtLeastTwoIPv4Octets(value) || hasAtLeastTwoIPv6Hextets(value);

export const isPingMeshIPAddress = (value: string): boolean => {
  const address = value.trim();
  const octets = address.split('.');
  if (octets.length === 4 && octets.every((octet) => /^\d{1,3}$/.test(octet) && Number(octet) <= 255)) {
    return true;
  }
  if (!address.includes(':') || typeof URL === 'undefined') {
    return false;
  }
  try {
    const parsed = new URL(`http://[${address}]/`);
    return parsed.hostname.startsWith('[') && parsed.hostname.endsWith(']');
  } catch {
    return false;
  }
};

/**
 * Ping Mesh endpoint suggestions are intentionally delayed until the user has
 * entered two IPv4 octets or two IPv6 hextets, avoiding broad queries.
 */
const PingMeshEndpointAutocomplete = ({
  side,
  label,
  placeholder,
  value,
  disabled = false,
  suggestionsEnabled = true,
  showLabel = true,
  inputClassName,
  buildRequest,
  search,
  onInputChange,
  onSelect
}: PingMeshEndpointAutocompleteProps) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<SearchItem[]>([]);
  const [coverage, setCoverage] = useState<SearchCoverage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const query = value.trim();
  const searchEnabled = suggestionsEnabled && hasPingMeshAddressPrefix(query);

  useEffect(() => {
    if (!open || disabled) {
      return;
    }
    if (!searchEnabled) {
      setItems([]);
      setCoverage(null);
      setError(null);
      setHighlightedIndex(0);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await search(buildRequest(query), controller.signal);
        if (controller.signal.aborted) {
          return;
        }
        setItems(response.items ?? []);
        setCoverage(response.coverage ?? null);
        setHighlightedIndex(0);
      } catch (searchError) {
        if (controller.signal.aborted || isAbortError(searchError)) {
          return;
        }
        setItems([]);
        setCoverage(null);
        setError(await resolveErrorMessage(searchError, 'Unable to load suggestions.'));
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }, 180);
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [buildRequest, disabled, open, query, search, searchEnabled]);

  useEffect(() => {
    if (value.trim().length === 0) {
      setItems([]);
      setCoverage(null);
      setError(null);
      setHighlightedIndex(0);
    }
  }, [value]);

  const selectItem = useCallback((item: SearchItem) => {
    onSelect(item);
    setOpen(false);
    setItems([]);
    setCoverage(null);
    setError(null);
    setHighlightedIndex(0);
  }, [onSelect]);

  const onKeyDown = useCallback((event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (!open) {
      if (event.key === 'ArrowDown' && items.length > 0) {
        event.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedIndex((current) => (items.length === 0 ? 0 : Math.min(current + 1, items.length - 1)));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex((current) => (items.length === 0 ? 0 : Math.max(current - 1, 0)));
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      return;
    }
    if (event.key === 'Enter' && items[highlightedIndex]) {
      event.preventDefault();
      selectItem(items[highlightedIndex]);
    }
  }, [highlightedIndex, items, open, selectItem]);

  const combobox = (
    <div
      ref={rootRef}
      className={`path-tracer__combobox${open ? ' is-open' : ''}${error ? ' is-error' : ''}`}
      onBlur={(event) => {
        const nextTarget = event.relatedTarget as Node | null;
        if (!rootRef.current?.contains(nextTarget)) {
          setOpen(false);
        }
      }}>
      <input
        type="text"
        className={inputClassName}
        value={value}
        disabled={disabled}
        autoComplete="off"
        spellCheck={false}
        placeholder={placeholder}
        aria-label={label}
        onFocus={() => {
          if (suggestionsEnabled) setOpen(true);
        }}
        onKeyDown={onKeyDown}
        onChange={(event) => {
          onInputChange(event.target.value);
          if (suggestionsEnabled) setOpen(true);
        }}
      />
      {suggestionsEnabled && (open || loading || Boolean(error)) && (
        <div className="path-tracer__menu" role="listbox" aria-label={`${label} suggestions`}>
          {loading && <div className="path-tracer__menu-state">Loading suggestions…</div>}
          {!loading && error && <div className="path-tracer__menu-state path-tracer__menu-state--error">{error}</div>}
          {!loading && !error && items.length === 0 && (
            <div className="path-tracer__menu-state">
              {searchEnabled ? `No ${side} matches for "${query}".` : 'Enter at least two IPv4 octets or two IPv6 hextets to see suggestions.'}
            </div>
          )}
          {!loading && !error && items.map((item, index) => {
            const siteLabel = item.site?.name?.trim() || item.site?.id?.trim() || '';
            return (
              <button
                key={`${item.kind}:${item.reference.id ?? item.reference.text ?? item.displayName}:${index}`}
                type="button"
                className={`path-tracer__menu-item${index === highlightedIndex ? ' is-active' : ''}`}
                role="option"
                aria-selected={index === highlightedIndex}
                onMouseDown={(event) => {
                  event.preventDefault();
                  selectItem(item);
                }}
                onMouseEnter={() => setHighlightedIndex(index)}>
                <span className="path-tracer__menu-title">
                  <strong>{item.displayName}</strong>
                  <span>{item.kind.replace(/_/g, ' ')}</span>
                </span>
                {(item.summary || siteLabel) && (
                  <span className="path-tracer__menu-summary">
                    {item.summary}
                    {item.summary && siteLabel ? ' • ' : ''}
                    {siteLabel}
                  </span>
                )}
              </button>
            );
          })}
          {!loading && !error && coverage?.scopeMode && (
            <div className="path-tracer__menu-footer">Coverage: {coverage.scopeMode.replace(/_/g, ' ')}</div>
          )}
        </div>
      )}
    </div>
  );

  if (!showLabel) {
    return combobox;
  }
  return (
    <label className="path-tracer__field">
      <span className="path-tracer__field-label">{label}</span>
      {combobox}
    </label>
  );
};

export default PingMeshEndpointAutocomplete;
